import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  createStyles,
  FileButton,
  Flex,
  Group,
  Loader,
  Skeleton,
  Text,
} from "@mantine/core";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { useSelector } from "react-redux";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { StaffDetailsTab } from "../../../components/StaffDetails/StaffDetailsTab";
import { StaffRolePermissionTab } from "../../../components/StaffDetails/StaffRolePermissionTab";
import { StaffDocumentsTab } from "../../../components/StaffDetails/StaffDocumentsTab";
import { StaffSettings } from "../../../components/StaffDetails/StaffSettings";
import { useForm } from "@mantine/form";
import {
  decrypt,
  encrypt,
  getInitialAdminPermissions,
  initialManagerPermissions,
  initialStaffPermissions,
  showError,
  usStates,
} from "../../../utils";

import {
  addStaff,
  editStaff,
  getStaffById,
  updateAssignedStaffPatients,
  uploadStaffProfile,
} from "../../../services/staff";
import { getAllFacilities, searchContractorByEmail, requestContractorAccess } from "../../../services";
import TableLoader from "../../../components/TableLoader";
import UploadStaffDoc from "../../../components/StaffDetails/UploadStaffDoc";
import { uploadFileToS3Api } from "../../../utils/PresignedUrlFunction";
import { getDashboardCounts } from "../../../services/dashboard";
import { IoSearchOutline } from "react-icons/io5";
import { Paper, TextInput, Divider } from "@mantine/core";
import swal from "sweetalert";
const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    height: "100%",
    minHeight: "100vh",
    flexDirection: "column",
    overflowX: "auto",
    backgroundColor: "#F5F5F5",
  },
  staffInfoContainer: {
    borderRadius: "10px",
    backgroundColor: "#FCFCFC",
    margin: "20px",
  },
  staffDetails: {
    display: "flex",
    alignItems: "center",
    padding: "20px",
    gap: "20px",
  },
  tabContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    gap: "20px",
    padding: "0 20px",
  },
  tab: {
    padding: "10px 0",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "all 0.2s ease",
    color: "#262626",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    textTransform: "capitalize",
  },
  activeTab: {
    fontWeight: 700,
    color: "#000",
    borderBottom: "2px solid #000",
  },
  disabledTab: {
    pointerEvents: "none",
    opacity: 0.5,
    cursor: "not-allowed",
    color: "#262626",
  },
  title: {
    margin: "0 20px",
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
  },
}));
const StaffDetails = () => {
  const { id, mode } = useParams(); // mode can be "view" or "edit"
  const { loading, staff, addStaffLoading } = useSelector((state) => state.staff);
  const [selectedStaff, setSelectedStaff] = useState(
    staff?.staffs?.find((s) => String(s.id) === String(id))
  );
 const { profile } = useSelector((state) => state?.profile);
  const { classes, cx } = useStyles();
  const navigate = useNavigate();

  const tabs = ["details", "roles and permissions", "settings", "documents"];
  const [activeTab, setActiveTab] = useState("details");
  const openRef = useRef();
  const [image, setImage] = useState(null);
  const [imageloading, setImageLoading] = useState(false);
  const [returnedId, setReturnedId] = useState();
  const [alreadyCreatedStaff, setAlreadyCreatedStaff] = useState(null);
  const edit = selectedStaff ? true : false;
  const [selectedClientRows, setSelectedClientRows] = useState([]);
  const [userpermissions, setUserPermissions] = useState();

  // Contractor search state (add mode only)
  const [contractorEmail, setContractorEmail] = useState("");
  const [contractorSearching, setContractorSearching] = useState(false);
  const [contractorResult, setContractorResult] = useState(null);
  const [contractorSearched, setContractorSearched] = useState(false);
  const [contractorRequestLoading, setContractorRequestLoading] = useState(false);

  const handleContractorSearch = async () => {
    if (!contractorEmail.trim()) return;
    setContractorSearching(true);
    setContractorResult(null);
    setContractorSearched(false);
    const result = await searchContractorByEmail(contractorEmail.trim());
    setContractorResult(result);
    setContractorSearched(true);
    setContractorSearching(false);
  };

  const handleAddContractor = async () => {
    if (!contractorResult?.id) return;
    setContractorRequestLoading(true);
    const res = await requestContractorAccess(contractorResult.id);
    setContractorRequestLoading(false);
    if (res) {
      swal({ title: "Request Sent", text: "A notification and email have been sent to the contractor.", icon: "success" });
      navigate("/home/staff");
    }
  };

  const location = useLocation();
  let notificationDoc = location.state?.notificationDoc;

  useEffect(() => {
    if (notificationDoc) {
      setSelectedStaff(notificationDoc?.staff);
    }
  }, []);

  useEffect(() => {
    setAlreadyCreatedStaff(null);
    getAllFacilities();
  }, []);

  useEffect(() => {
    if (mode === "view" || mode === "edit") {
      const staffId = selectedStaff?.id || id;
      if (staffId) {
        getStaffById(staffId).then((staff) => {
          if (staff && !selectedStaff) {
            setSelectedStaff(staff);
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    if (selectedStaff?.photo && !image) {
      setImage(selectedStaff?.photo);
    }
    if (selectedStaff && selectedStaff?.role === "admin") {
      setUserPermissions(getInitialAdminPermissions(selectedStaff));
    } else if (selectedStaff && selectedStaff?.role === "manager") {
      setUserPermissions(initialManagerPermissions);
    } else {
      setUserPermissions(initialStaffPermissions);
    }
  }, [selectedStaff]);

  useEffect(() => {
    if (notificationDoc) {
      setActiveTab("documents");
    }
  }, [notificationDoc, location]);

  const form = useForm({
    mode: "uncontrolled",

    initialValues: {
      fullName: edit ? selectedStaff?.name : "",
      firstName: edit
        ? selectedStaff?.firstName?.split(" ")?.[0] ||
          selectedStaff?.name?.split(" ")?.[0]
        : "",
      lastName: edit
        ? selectedStaff?.lastName ||
          selectedStaff?.firstName?.split(" ")?.slice(1).join(" ") ||
          selectedStaff?.name?.split(" ")?.slice(1).join(" ") ||
          ""
        : "",

      position: edit ? selectedStaff?.position : "",
      email: edit ? selectedStaff?.email : "",
      // phone: edit ? Number(selectedStaff?.phone || "") : "",
      phone: edit
        ? selectedStaff?.phone
          ? Number(selectedStaff.phone)
          : ""
        : "",

      role: edit ? selectedStaff?.role : "",
      facilityId: edit ? selectedStaff?.facilityId?.map((f) => f?.id) : [],
       is2FaAccess: edit ? selectedStaff?.is2FaAccess: false
    },

    validate: {
      firstName: (value) => (value ? null : "Required"),
      lastName: (value) => (value ? null : "Required"),
      position: (value) => (value ? null : "Required"),
      email: (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(value)
          ? null
          : "Please enter a valid email address";
      },
      phone: (value) => {
        if (!value) return null;
        const phoneRegex = /^\+?[1-9]\d{6,14}$/;
        return phoneRegex.test(value)
          ? null
          : "Enter a valid phone number (e.g. +14155552671)";
      },
    },
   
  });

  const [addedFacilities, setAddedFacilities] = useState(
    selectedStaff?.facilityId?.map((f) => ({
      value: f.id,
      label: f.name,
    })) || []
  );

  const [role, setRole] = useState("staff");
  const [staffResponse, setStaffResponse] = useState();
  const [roleError, setRoleError] = useState(false);

  const handleSave = async (values) => {
    let payload = {
      ...form.values,
      facilityId: addedFacilities?.map((i) => i?.value),
      role,
      permissions: userpermissions,
      photo: image,
      // fullName: `${form.values.firstName} ${form.values.lastName}`,
    };
    if (activeTab === "details") {
      const validation = form.validate();
      if (!validation.hasErrors) {
        setActiveTab("roles and permissions");
      }
    } else if (activeTab === "roles and permissions") {
      if (role) {
        setRoleError(false);
        setActiveTab("settings");
      } else {
        setRoleError(true);
      }
    } else if (activeTab === "settings") {
      if (mode === "edit" || alreadyCreatedStaff) {
        payload = {
          ...payload,
          name: `${form.values.firstName} ${form.values.lastName}`,
          id,
        };
        let response = await editStaff(payload, true, activeTab);
        if (response) {
          const assignedPayload = {
            clients: selectedClientRows || [],
            staff: selectedStaff?.id,
          };
          await updateAssignedStaffPatients(assignedPayload);
          setSelectedClientRows([]);
          handleCancel();
        }
      } else {
        payload = {
          ...payload,
          fullName: `${form.values.firstName} ${form.values.lastName}`,
          active: false,
          signatureActive: false,
        };
        let response = await addStaff(payload, activeTab);
        if (response?.staff) {
          setReturnedId(response?.staff);
          setAlreadyCreatedStaff(response?.staff);
          const assignedPayload = {
            clients: selectedClientRows || [],
            staff: response?.staff?.id,
          };
          await updateAssignedStaffPatients(assignedPayload);
          setSelectedClientRows([]);
          handleCancel();
        }
      }
    }
  };
  const handleCancel = () => {
    navigate("/home/staff");
    form.reset();
    setAddedFacilities(selectedStaff?.facilityId || []);
  };

  // // Function to handle image upload and processing
  // const onChangePhoto = async (files) => {
  //   setImageLoading(true);
  //   if (files) {
  //     const formData = new FormData();
  //     formData.append("file", files);
  //     const localImageUrl = await uploadStaffProfile(formData); // Generate blob URL
  //     const encryptedImageUrl = encrypt(localImageUrl?.imageUrl); // If needed
  //     setImage(encryptedImageUrl);
  //     setImageLoading(false);
  //   }
  //   if (openRef.current) {
  //     openRef.current();
  //   }
  // };

  const onChangePhoto = async (files) => {
    if (!files) return;
    try {
      setImageLoading(true);

      // 1. Upload to S3
      const result = await uploadFileToS3Api({ file: files });

      // 2. Encrypt the image URL (if needed)
      const encryptedImageUrl = encrypt(result?.uploadURL);

      // 3. Update UI with image
      setImage(encryptedImageUrl);

      // 4. Trigger modal or UI action
      if (openRef.current) {
        openRef.current();
      }
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Box fluid className={classes.content}>
      <AppNavbar title={`${mode} Staff`} profile={profile} />
      <Flex
        align="center"
        gap={5}
        m={10}
        onClick={() => navigate("/home/staff")}
        style={{ cursor: "pointer", width: "fit-content" }}
      >
        <MdKeyboardArrowLeft size={30} />
        <Text fw={400} fz={14} c={"#262626"} ff={"Inter"}>
          Back
        </Text>
      </Flex>
      <Box className={classes.staffInfoContainer}>
        {loading ? (
          <Skeleton
            height={60}
            radius="sm"
            style={{ margin: "10px", width: "200px" }}
          />
        ) : (
          <Box className={classes.staffDetails}>
            {imageloading ? (
              <Loader size="md" radius="100%" color="yellow" />
            ) : (
              <Avatar
                src={decrypt(image)}
                size={50}
                radius={50}
                shape="circle"
              />
            )}
            {mode !== "view" && (
              <Group>
                <FileButton
                  onChange={(files) => onChangePhoto(files)}
                  openRef={openRef}
                  accept="image/*"
                >
                  {(props) => (
                    <Button
                      variant="filled"
                      color="gray"
                      size="xs"
                      radius="md"
                      {...props}
                    >
                      {edit ? "Change Photo" : "Add Photo"}
                    </Button>
                  )}
                </FileButton>
              </Group>
            )}
            {mode === "add" && (
              <Box className={classes.staffInfo}>
                <Text variant="text" c={"#737373"} fw={400} fz={12}>
                  Staff name
                </Text>
                <Text variant="text" fz={16} fw={600} ff={"Inter"}>
                  {`${form.getTransformedValues()?.firstName} ${
                    form.getTransformedValues()?.lastName
                  }`}
                </Text>
                <Text
                  variant="text"
                  fz={14}
                  fw={500}
                  c={"#737373"}
                  ff={"Inter"}
                  transform={"uppercase"}
                >
                  {selectedStaff?.id?.slice(0, 6)}
                </Text>
              </Box>
            )}
            {mode !== "add" && (
              <Box className={classes.staffInfo}>
                <Text variant="text" c={"#737373"} fw={400} fz={12}>
                  Staff name
                </Text>
                <Text variant="text" fz={16} fw={600} ff="Inter">
                  {selectedStaff?.firstName && selectedStaff?.lastName
                    ? `${selectedStaff.firstName} ${selectedStaff.lastName}`
                    : selectedStaff?.name}
                </Text>
                <Text
                  variant="text"
                  fz={14}
                  fw={500}
                  c={"#737373"}
                  ff={"Inter"}
                  transform={"uppercase"}
                >
                  {selectedStaff?.id?.slice(0, 6)}
                </Text>
              </Box>
            )}
          </Box>
        )}

        {/* Custom Tabs */}
        <Box className={classes.tabContainer}>
          {tabs?.map((tab, index) => (
            <Text
              key={tab}
              className={cx(classes.tab, {
                [classes.activeTab]: activeTab === tab,
                [classes.disabledTab]:
                  mode === "add" && index > tabs.indexOf(activeTab),
              })}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </Text>
          ))}
        </Box>
      </Box>
      {/* <Text className={classes.title}>Staff Information</Text> */}

      {/* Tab Content */}
      <Box className={classes.staffInfoContainer}>
        {loading ? (
          <TableLoader />
        ) : (
          <Box px={20} pb={20}>
            {/* {mode === "add" && activeTab === "details" && (
              <Box
                mb={0}
                sx={{
                  width: "100%",
                  marginBlock: "10px",
                  padding: "16px 23px 18px 23px",
                  borderRadius: 16,
                  border: "1px solid #FFF",
                  backgroundColor: "#FCFCFC",
                }}
              >
                <Text fz={14} fw={600} color="#344054" ff="Inter" mb={12}>
                  Search for an existing contractor
                </Text>
                <Flex gap={12} align="center">
                  <TextInput
                    placeholder="Enter contractor email address"
                    value={contractorEmail}
                    onChange={(e) => {
                      setContractorEmail(e.target.value);
                      setContractorSearched(false);
                      setContractorResult(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleContractorSearch();
                      }
                    }}
                    icon={<IoSearchOutline size={16} color="#94A3B8" />}
                    styles={{
                      root: { flex: 1 },
                      input: {
                        height: 44,
                        borderRadius: 8,
                        border: "1px solid #CBD5E1",
                        fontSize: 14,
                        fontFamily: "Inter",
                        "&::placeholder": { color: "#A3A3A3" },
                      },
                    }}
                  />
                  <Button
                    onClick={handleContractorSearch}
                    loading={contractorSearching}
                    styles={{
                      root: {
                        height: 44,
                        minWidth: 100,
                        borderRadius: 8,
                        backgroundColor: "#FFAF4A",
                        border: "1px solid #FFAF4A",
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter",
                        color: "#262626",
                        "&:hover": { backgroundColor: "#FFAF4A", opacity: 0.8 },
                      },
                    }}
                  >
                    Search
                  </Button>
                </Flex>
                {contractorSearched && contractorResult && (
                  <Box
                    mt={16}
                    p={16}
                    sx={{
                      borderRadius: 8,
                      border: "1px solid #E5E5E5",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Text fz={15} fw={600} ff="Inter" color="#171717">
                          {contractorResult.firstName || contractorResult.name?.split(" ")?.[0]}{" "}
                          {contractorResult.lastName || contractorResult.name?.split(" ")?.slice(1)?.join(" ")}
                        </Text>
                        {contractorResult.businessName && (
                          <Text fz={13} fw={400} ff="Inter" color="#667085" mt={2}>
                            {contractorResult.businessName}
                          </Text>
                        )}
                        <Text fz={13} fw={400} ff="Inter" color="#667085" mt={2}>
                          {contractorResult.email}
                        </Text>
                      </Box>
                      <Button
                        onClick={handleAddContractor}
                        loading={contractorRequestLoading}
                        styles={{
                          root: {
                            height: 38,
                            borderRadius: 8,
                            backgroundColor: "#FFAF4A",
                            border: "1px solid #FFAF4A",
                            fontSize: 14,
                            fontWeight: 600,
                            fontFamily: "Inter",
                            color: "#262626",
                            "&:hover": { backgroundColor: "#FFAF4A", opacity: 0.8 },
                          },
                        }}
                      >
                        Add Contractor
                      </Button>
                    </Flex>
                  </Box>
                )}
                {contractorSearched && !contractorResult && !contractorSearching && (
                  <Text fz={13} fw={400} ff="Inter" color="#667085" mt={16} align="center">
                    No contractor found with that email.
                  </Text>
                )}
              </Box>
            )}
            {mode === "add" && activeTab === "details" && (
              <Text fz={14} fw={600} color="#344054" ff="Inter" mb={12} mt={10}>
                Or add a new staff member
              </Text>
            )} */}
            {activeTab === "details" && (
              <StaffDetailsTab
                data={selectedStaff}
                mode={mode}
                form={form}
                setSelectedStaff={setSelectedStaff}
                handleSave={handleSave}
                handleCancel={handleCancel}
              />
            )}
            {activeTab === "roles and permissions" && (
              <StaffRolePermissionTab
                data={selectedStaff}
                mode={mode}
                role={role}
                setRole={setRole}
                form={form}
                handleSave={handleSave}
                activeTab={activeTab}
                roleError={roleError}
                userpermissions={userpermissions}
                setUserPermissions={setUserPermissions}
              />
            )}
            {activeTab === "documents" && (
              <StaffDocumentsTab
                userId={selectedStaff?.id || returnedId?.id || id}
                userType="staff"
              />
            )}
            {activeTab === "settings" && (
              <StaffSettings
                data={selectedStaff}
                mode={mode}
                handleCancel={handleCancel}
                staffResponse={returnedId}
                activeTab={activeTab}
                handleSave={handleSave}
                form={form}
                setAddedFacilities={setAddedFacilities}
                addedFacilities={addedFacilities}
                selectedClientRows={selectedClientRows}
                setSelectedClientRows={setSelectedClientRows}
                loading={addStaffLoading}
              />
            )}
          </Box>
        )}
      </Box>
      {activeTab === "documents" && notificationDoc ? (
        <UploadStaffDoc
          selectedFolder={notificationDoc?.metadata?.folder}
          userId={notificationDoc?.staff?.id}
          userType={notificationDoc?.userType}
          isDocument={true}
          uploadedFiles={notificationDoc?.metadata?.file}
          isnotificationdoc={true}
        />
      ) : null}
    </Box>
  );
};

export default StaffDetails;
